import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { TwoDIcon } from "../icons";
import { CoverFile } from "./types";

const PlaceholderCard = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  backgroundColor: theme.palette.secondary[5],
  borderRadius: theme.spacing(3),
}));

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "numImages",
})<{ numImages: number }>(({ theme, numImages }) => {
  return {
    display: "grid",
    gridTemplateColumns: numImages > 1 ? "3fr 1fr" : "1fr",
    gridTemplateRows: numImages > 1 ? "1fr 1fr 1fr" : "1fr",
    gridTemplateAreas:
      numImages > 1
        ? '"primary secondary1" "primary secondary2" "primary secondary3"'
        : '"primary"',
    height: "100%",
    width: "100%",
    borderRadius: theme.spacing(3),
    overflow: "hidden",
    gap: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gridTemplateRows: new Array(4)
        .fill("0fr")
        .map((defaultValue, index) => {
          if (index >= numImages) {
            return defaultValue;
          }
          return "1fr";
        })
        .join(" "),
      gridTemplateAreas: '"primary" "secondary1" "secondary2" "secondary3"',
    },
  };
});

const ImageBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "index",
})<{ index: number }>(({ index }) => ({
  overflow: "hidden",
  gridArea: index === 0 ? "primary" : `secondary${index}`,
}));

const Image = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
}));

interface CoverFilesContentProps {
  files: CoverFile[];
}

export const CoverFilesContent = (props: CoverFilesContentProps) => {
  const { files } = props;

  if (files.length === 0) {
    return (
      <PlaceholderCard>
        <TwoDIcon sx={{ width: 40, height: 40 }} />
        <Typography variant="regular">No cover images set</Typography>
      </PlaceholderCard>
    );
  }

  const coverFiles = new Array(4).fill(null).map<CoverFile | null>((_, ix) => {
    if (ix >= files.length) {
      return null;
    }
    return files[ix];
  });

  return (
    <Container numImages={files.length}>
      {coverFiles.map((file, index) => (
        <ImageBox index={index} key={file?.id ?? index}>
          {file && <Image src={file.previewUrl ?? undefined} alt={file.name} />}
        </ImageBox>
      ))}
    </Container>
  );
};
